import {
  attributeDisplayLabel,
  attributeValueDisplayLabel,
} from '@/tiktok-platform/utils/tiktokAttributeTemplate';
import { skcHasColor } from '@/tiktok-platform/modules/products/components/SpecSupplySection';
import { resolvePublishSlotImageUrl } from '@/tiktok-platform/utils/tiktokPublishImageSlots';
import { appendCopywritingMaxCharsToPrompt } from '@/pages/ipe/workbench/constants/aiCopywritingConfig';
import { resolveImg2ImgReferenceForApi } from '@/pages/ipe/workbench/utils/img2imgPublicUrl';
import { aiCopywritingAPI } from '@/utils/api';
import {
  TIKTOK_DESCRIPTION_MAX_CHARS,
  TIKTOK_TITLE_MAX_CHARS_DEFAULT,
  resolveTiktokTitleLengthBounds,
} from '@/tiktok-platform/utils/tiktokPublishFormLimits';
import { fixedUi } from '@/i18n/fixedUi';

/** 描述生成参考主图上限（过多会分批，影响耗时） */
export const TIKTOK_AI_DESCRIPTION_MAX_IMAGES = 9;

/** @deprecated 使用 resolveTiktokTitleLengthBounds；默认 255（非 Temu 250） */
export const TIKTOK_PUBLISH_TITLE_MAX_CHARS = TIKTOK_TITLE_MAX_CHARS_DEFAULT;

function buildBasePrompt(maxChars) {
  return (
    `根据商品主图，结合以下类目与商品属性，生成一条英文电商平台商品名称。要求：全英文、简洁有吸引力、突出卖点、不含中文、不超过${maxChars}字符，不要加引号或前缀。`
  );
}

function normalizeGeneratedTitle(raw) {
  const text = String(raw || '').trim();
  return text.replace(/^(?:商品标题|产品标题|商品名称|标题|Product Title|Title)\s*[:：]\s*/i, '').trim();
}

function findAttributeValueName(attr, valueId) {
  const id = String(valueId || '').trim();
  if (!id) return '';
  const values = Array.isArray(attr?.values) ? attr.values : [];
  const hit = values.find((v) => String(v?.id) === id);
  return hit ? attributeValueDisplayLabel(hit) : '';
}

function resolveStoredAttributeValueText(attr, raw) {
  if (Array.isArray(raw)) {
    const parts = raw.map((row) => {
      if (row && typeof row === 'object') {
        const vid = String(row.valueId ?? row.attributeValueId ?? '').trim();
        const pct = String(row.percent ?? row.attributeExtraValue ?? '').trim();
        const name = findAttributeValueName(attr, vid) || vid;
        return pct ? `${name} ${pct}%` : name;
      }
      return findAttributeValueName(attr, row) || String(row || '').trim();
    }).filter(Boolean);
    return parts.join(', ');
  }
  if (raw && typeof raw === 'object') {
    const vid = String(raw.valueId ?? raw.attributeValueId ?? '').trim();
    const extra = String(raw.attributeExtraValue ?? raw.percent ?? '').trim();
    if (vid) {
      const name = findAttributeValueName(attr, vid) || vid;
      return extra ? `${name} ${extra}` : name;
    }
    return extra;
  }
  const text = String(raw ?? '').trim();
  return findAttributeValueName(attr, text) || text;
}

export function formatFilledProductAttributeLines(productAttributes = {}, attrDefs = []) {
  const attrs = Array.isArray(attrDefs) ? attrDefs : [];
  const lines = [];
  attrs.forEach((attr) => {
    const id = String(attr?.attributeId || '').trim();
    if (!id) return;
    const raw = productAttributes?.[id];
    if (raw == null || raw === '') return;
    const label = attributeDisplayLabel(attr) || id;
    const valueText = resolveStoredAttributeValueText(attr, raw);
    if (valueText) lines.push(`${label}: ${valueText}`);
  });
  return lines;
}

export function buildTiktokPublishTitlePrompt({
  categoryPathLabel = '',
  productAttributes = {},
  attrDefs = [],
  maxChars = TIKTOK_TITLE_MAX_CHARS_DEFAULT,
} = {}) {
  const limit = Number(maxChars) > 0 ? Number(maxChars) : TIKTOK_TITLE_MAX_CHARS_DEFAULT;
  const lines = [];
  const category = String(categoryPathLabel || '').trim();
  if (category) lines.push(`类目：${category}`);
  const attrLines = formatFilledProductAttributeLines(productAttributes, attrDefs);
  if (attrLines.length) {
    lines.push('商品属性：');
    attrLines.forEach((line) => lines.push(`- ${line}`));
  }
  const context = lines.join('\n');
  const base = buildBasePrompt(limit);
  const body = context ? `${base}\n\n${context}` : base;
  return appendCopywritingMaxCharsToPrompt(body, limit, 500);
}

/** 第一个已选主规格颜色的主图 URL */
export function resolveFirstSkcMainImageUrl(skcList = []) {
  const list = Array.isArray(skcList) ? skcList : [];
  for (const skc of list) {
    if (!skcHasColor(skc)) continue;
    const url = resolvePublishSlotImageUrl(skc.mainImage);
    if (url) return url;
  }
  return '';
}

/** 当前款式下所有已选颜色的主图 URL（去重，最多 9 张） */
export function resolveAllSkcMainImageUrls(skcList = [], { max = TIKTOK_AI_DESCRIPTION_MAX_IMAGES } = {}) {
  const list = Array.isArray(skcList) ? skcList : [];
  const urls = [];
  const limit = Number(max) > 0 ? Number(max) : TIKTOK_AI_DESCRIPTION_MAX_IMAGES;
  for (const skc of list) {
    if (!skcHasColor(skc)) continue;
    const url = resolvePublishSlotImageUrl(skc.mainImage);
    if (!url || urls.includes(url)) continue;
    urls.push(url);
    if (urls.length >= limit) break;
  }
  return urls;
}

function collectSkcColorLabels(skcList = []) {
  const labels = [];
  (Array.isArray(skcList) ? skcList : []).forEach((skc) => {
    if (!skcHasColor(skc)) return;
    const name = String(skc?.colorLabel || skc?.colorExtraValue || '').trim();
    if (name && !labels.includes(name)) labels.push(name);
  });
  return labels;
}

function buildDescriptionBasePrompt(maxChars = TIKTOK_DESCRIPTION_MAX_CHARS) {
  const limit = Number(maxChars) > 0 ? Math.floor(Number(maxChars)) : TIKTOK_DESCRIPTION_MAX_CHARS;
  const idealHi = Math.min(1500, Math.max(400, limit));
  return [
    'You are a TikTok Shop listing copywriter.',
    'Based on the product main images AND the text context below, write an English product DESCRIPTION for TikTok Shop Create Product.',
    'Goals: highlight selling points, materials/feel, fit/style, use occasions, and buyer-care details customers love to see.',
    'Rules:',
    '- English only (no Chinese characters).',
    '- Output simple HTML only: use <p>, <ul>, <li>, <strong>, <br> when helpful. No markdown fences, no title/prefix.',
    '- Prefer 3–6 short paragraphs or a short intro + bullet selling points.',
    '- Be specific to what is visible in the images and stated in attributes; do not invent certifications or materials.',
    `- Ideal length about 400–${idealHi} characters (HTML tags count); hard max ${limit} characters. Do not exceed ${limit}.`,
  ].join('\n');
}

export function buildTiktokPublishDescriptionPrompt({
  categoryPathLabel = '',
  productAttributes = {},
  attrDefs = [],
  brandLabel = '',
  productTitle = '',
  skcList = [],
  maxChars = TIKTOK_DESCRIPTION_MAX_CHARS,
} = {}) {
  const lines = [];
  const title = String(productTitle || '').trim();
  if (title) lines.push(`Product title: ${title}`);
  const brand = String(brandLabel || '').trim();
  if (brand) lines.push(`Brand: ${brand}`);
  const category = String(categoryPathLabel || '').trim();
  if (category) lines.push(`Category: ${category}`);
  const colors = collectSkcColorLabels(skcList);
  if (colors.length) lines.push(`Colors / variants: ${colors.join(', ')}`);
  const attrLines = formatFilledProductAttributeLines(productAttributes, attrDefs);
  if (attrLines.length) {
    lines.push('Product attributes:');
    attrLines.forEach((line) => lines.push(`- ${line}`));
  }
  const context = lines.join('\n');
  const base = buildDescriptionBasePrompt(maxChars);
  // 描述 prompt 本身可稍长；不用标题那套 maxChars≤500 的 append 后缀
  const body = context ? `${base}\n\nContext:\n${context}` : base;
  return body.slice(0, 4000);
}

function normalizeGeneratedDescription(raw) {
  let text = String(raw || '').trim();
  text = text
    .replace(/^```(?:html|HTML)?\s*/i, '')
    .replace(/\s*```$/i, '')
    .replace(/^(?:Product Description|Description|商品描述|描述)\s*[:：]\s*/i, '')
    .trim();
  if (!text) return '';
  if (/<\s*(p|div|ul|ol|br|h[1-6]|strong|em|li)\b/i.test(text)) {
    return text.slice(0, TIKTOK_DESCRIPTION_MAX_CHARS);
  }
  const paragraphs = text
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (!paragraphs.length) return '';
  const html = paragraphs
    .map((p) => {
      if (/^[-*•]\s+/m.test(p) || /^\d+\.\s+/m.test(p)) {
        const items = p.split(/\n/).map((line) => line.replace(/^[-*•]\s+/, '').replace(/^\d+\.\s+/, '').trim()).filter(Boolean);
        if (items.length > 1) {
          return `<ul>${items.map((item) => `<li>${item}</li>`).join('')}</ul>`;
        }
      }
      return `<p>${p.replace(/\n/g, '<br>')}</p>`;
    })
    .join('');
  return html.slice(0, TIKTOK_DESCRIPTION_MAX_CHARS);
}

/**
 * 调用 AI 文案计费接口生成英文标题（按张扣 IPE 豆，与工作室 AI 文案同价）。
 * 若模型仍返回中文，再经 DeepSeek 强制译英。
 */
export async function generateTiktokPublishAiTitle({
  skcList = [],
  categoryPathLabel = '',
  productAttributes = {},
  attrDefs = [],
  region = '',
  titleMaxLength = 0,
  maxChars,
} = {}) {
  const imageUrl = resolveFirstSkcMainImageUrl(skcList);
  if (!imageUrl) {
    throw new Error(fixedUi('请先为第一个颜色上传主图'));
  }

  const publicRef = await resolveImg2ImgReferenceForApi(imageUrl);
  if (!publicRef) {
    throw new Error(fixedUi('主图无法解析，请重新上传'));
  }

  const limit = Number(maxChars) > 0
    ? Number(maxChars)
    : resolveTiktokTitleLengthBounds(region, titleMaxLength).max;

  const prompt = buildTiktokPublishTitlePrompt({
    categoryPathLabel,
    productAttributes,
    attrDefs,
    maxChars: limit,
  });

  const result = await aiCopywritingAPI.submitAndWait({
    requestId: `tiktok-publish-title-${Date.now()}`,
    prompt,
    referenceUrls: [publicRef],
    outputCount: 1,
    language: 'en',
    maxChars: limit,
  });

  let title = normalizeGeneratedTitle(result?.content || result?.text || '');
  if (!title) throw new Error(fixedUi('AI 未返回标题'));

  title = await translateTiktokTitleToEnglish(title, limit);
  if (!title) throw new Error(fixedUi('AI 未返回英文标题'));

  return title.slice(0, limit);
}

/**
 * 调用 AI 文案计费接口生成英文商品描述。
 * 参考当前款式全部主图 + 类目/属性/品牌/标题上下文；输出简单 HTML。
 */
export async function generateTiktokPublishAiDescription({
  skcList = [],
  categoryPathLabel = '',
  productAttributes = {},
  attrDefs = [],
  brandLabel = '',
  productTitle = '',
  maxChars = TIKTOK_DESCRIPTION_MAX_CHARS,
} = {}) {
  const imageUrls = resolveAllSkcMainImageUrls(skcList);
  if (!imageUrls.length) {
    throw new Error(fixedUi('请先为至少一个颜色上传主图'));
  }

  const publicRefs = [];
  for (const imageUrl of imageUrls) {
    const publicRef = await resolveImg2ImgReferenceForApi(imageUrl);
    if (publicRef && !publicRefs.includes(publicRef)) publicRefs.push(publicRef);
  }
  if (!publicRefs.length) {
    throw new Error(fixedUi('主图无法解析，请重新上传'));
  }

  const limit = Number(maxChars) > 0 ? Math.floor(Number(maxChars)) : TIKTOK_DESCRIPTION_MAX_CHARS;
  const prompt = buildTiktokPublishDescriptionPrompt({
    categoryPathLabel,
    productAttributes,
    attrDefs,
    brandLabel,
    productTitle,
    skcList,
    maxChars: limit,
  });

  // 后端 maxChars 上限 500 且文案偏「标题」；描述硬上限写在 prompt 里，这里不传 maxChars
  const result = await aiCopywritingAPI.submitAndWait({
    requestId: `tiktok-publish-desc-${Date.now()}`,
    prompt,
    referenceUrls: publicRefs,
    outputCount: 1,
    language: 'en',
  });

  let description = normalizeGeneratedDescription(result?.content || result?.text || '');
  if (!description) throw new Error(fixedUi('AI 未返回商品描述'));

  // 若仍含中文，整段译英后再包一层 HTML
  const { titleContainsCjk } = await import('@/pages/ipe/workbench/utils/ensureEnglishListingTitle');
  const plain = description.replace(/<[^>]+>/g, ' ');
  if (titleContainsCjk(plain)) {
    const en = await translateTiktokTitleToEnglish(plain, Math.min(Number(maxChars) || TIKTOK_DESCRIPTION_MAX_CHARS, 2000));
    description = normalizeGeneratedDescription(en);
  }
  if (!description) throw new Error(fixedUi('AI 未返回英文商品描述'));
  return description.slice(0, Number(maxChars) > 0 ? Number(maxChars) : TIKTOK_DESCRIPTION_MAX_CHARS);
}

/**
 * 确保标题为英文（DeepSeek /listing-export/translate-title）。
 * 已是英文则原样返回；含中文则译英。
 */
export async function translateTiktokTitleToEnglish(title, maxChars = TIKTOK_TITLE_MAX_CHARS_DEFAULT) {
  const text = String(title || '').trim();
  if (!text) return '';
  const limit = Number(maxChars) > 0 ? Number(maxChars) : TIKTOK_TITLE_MAX_CHARS_DEFAULT;
  try {
    const { ensureEnglishListingTitle, titleContainsCjk } = await import(
      '@/pages/ipe/workbench/utils/ensureEnglishListingTitle'
    );
    if (!titleContainsCjk(text)) {
      return text.slice(0, limit);
    }
    const en = await ensureEnglishListingTitle(text, {
      failMessage: fixedUi('商品名称须为英文；自动译英失败，请改为英文后重试'),
    });
    return String(en || '').trim().slice(0, limit);
  } catch (error) {
    const msg = String(error?.message || '');
    if (/未能译成英文|不能包含中文|自动翻译成英文失败|须为英文/.test(msg)) {
      throw new Error(fixedUi('商品名称须为英文；自动译英失败，请改为英文后重试'));
    }
    throw new Error(msg || fixedUi('商品名称须为英文；自动译英失败，请改为英文后重试'));
  }
}
